import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useViewerRoles, useWorkspace } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Paragraph,
	Separator,
	Spinner,
	Switch,
	tokens,
} from '@treams/treams-ui';

const SpinnerWrapper = styled(Box)`
	position: absolute;
	right: -25px;
	top: 3px;
`;

const StyledLink = styled.a`
	text-decoration: underline;
	text-align: center;
	opacity: 0.77;
	color: ${tokens.text.secondary};
`;

export const AIConfiguration = () => {
	const { t } = useTranslation();

	const workspace = useWorkspace();
	const viewerRoles = useViewerRoles();

	const [approve, { loading: approving }] = graphql.useApproveFeatureForViewerMutation();
	const [revoke, { loading: revoking }] = graphql.useRevokeFeatureApprovalForViewerMutation();

	const [approvalBeforeUpdating, setApprovalBeforeUpdating] = useState<boolean | undefined>(
		undefined,
	);

	const [error, attempt] = useErrorBoundary();

	const {
		data,
		loading: loadingFeatures,
		refetch: refetchFeatures,
	} = graphql.useWorkspaceFeaturesQuery({
		variables: {
			id: workspace.id,
		},
	});

	const conversationAIInsightsFeature = data?.workspace.features.find(
		(feature) => feature.feature.id === graphql.FeatureName.ConversationAiInsights,
	);

	const conversationAIInsightsEnabled =
		conversationAIInsightsFeature?.status === graphql.WorkspaceFeatureStatus.Enabled ||
		(viewerRoles.isWorkspaceSuperuser &&
			conversationAIInsightsFeature?.status === graphql.WorkspaceFeatureStatus.Superuser);

	if (!conversationAIInsightsEnabled || !conversationAIInsightsFeature) {
		return null;
	}

	const approveFeatureConditions = attempt(async () => {
		if (
			!conversationAIInsightsFeature.needsViewerApproval ||
			!conversationAIInsightsFeature.requiredConditions
		) {
			throw new Error('Cannot approve feature');
		}

		setApprovalBeforeUpdating(conversationAIInsightsFeature.needsViewerApproval);

		const result = await approve({
			variables: {
				input: {
					featureID: graphql.FeatureName.ConversationAiInsights,
					conditionsVersion: conversationAIInsightsFeature.requiredConditions.currentVersion,
				},
			},
		});

		if (result.data?.approveFeatureForViewer.approval) {
			refetchFeatures();
		}
	});

	const revokeFeatureApproval = attempt(async () => {
		setApprovalBeforeUpdating(conversationAIInsightsFeature.needsViewerApproval);

		const result = await revoke({
			variables: {
				input: {
					featureID: graphql.FeatureName.ConversationAiInsights,
				},
			},
		});

		if (result.data?.revokeFeatureApprovalForViewer.success) {
			refetchFeatures();
		}
	});

	const needsViewerApproval = conversationAIInsightsFeature.needsViewerApproval;
	const requiredConditions = conversationAIInsightsFeature.requiredConditions;

	const needsApprovalButCant = needsViewerApproval && !requiredConditions;

	const waitingForApprovalToChange = approvalBeforeUpdating === needsViewerApproval;

	const loading = loadingFeatures || approving || revoking;

	const disabled = loading || needsApprovalButCant || waitingForApprovalToChange;

	return (
		<>
			<GraphQLErrorMessage error={error} />
			<Heading>{t('personalSettings.ai.approvals.title')}</Heading>
			<Box maxWidth="600px">
				<FlexGap gap={4} justifyContent="space-between">
					<Box>
						<Paragraph>{t('personalSettings.ai.approvals.aiInsights.title')}</Paragraph>
					</Box>
					<FlexGap alignItems="center" gap={10} position="relative">
						<Paragraph>
							{needsViewerApproval
								? t('personalSettings.ai.approvals.noPermission')
								: t('personalSettings.ai.approvals.permission')}
						</Paragraph>
						<Switch
							on={!needsViewerApproval}
							onChange={(value) => {
								if (value) {
									approveFeatureConditions();
								} else {
									revokeFeatureApproval();
								}
							}}
							disabled={disabled}
						/>
						{loading && (
							<SpinnerWrapper>
								<Spinner />
							</SpinnerWrapper>
						)}
					</FlexGap>
				</FlexGap>
				<Separator mt={4} />
				<Flex justifyContent="center" mt={3}>
					{requiredConditions?.linkToConditions && (
						<StyledLink
							href={requiredConditions?.linkToConditions}
							target="_blank"
							rel="noreferrer"
						>
							{t('personalSettings.ai.approvals.aiInsights.link')}
						</StyledLink>
					)}
				</Flex>
			</Box>
		</>
	);
};
